import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { Switch } from "../components/ui/switch";
import { Separator } from "../components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import BrandLogo from "../components/BrandLogo";
import { BRAZILIAN_STATES, buildCityLabel, fetchCitiesByUf } from "../lib/brLocations";
import { toast } from "sonner";
import { AlertCircle, ArrowLeft, Briefcase, Mail, MapPin, Lock, User } from "lucide-react";

const AREAS = [
  "Financeiro",
  "Planejamento Estratégico",
  "Gestão Comercial",
  "Recursos Humanos",
  "Administração",
  "Produção",
  "Logística",
  "Tecnologia",
  "Jurídico",
  "Marketing",
  "Compras",
  "NR-1 Gestão de Riscos Ocupacionais",
  "Visão de Futuro",
];

const getAreaLabel = (area) => (area === "Tecnologia" ? "Tecnologia/IA" : area);

const ConsultantPartnerRegisterPage = () => {
  const navigate = useNavigate();
  const { registerConsultant, logout, user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [consent, setConsent] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    state_uf: "",
    city: "",
    online_only: false,
    bio: "",
    areas_supported: [],
  });
  const [cityOptions, setCityOptions] = useState([]);
  const [loadingCities, setLoadingCities] = useState(false);

  useEffect(() => {
    if (!formData.state_uf) {
      setCityOptions([]);
      return;
    }

    let isActive = true;
    setLoadingCities(true);

    fetchCitiesByUf(formData.state_uf)
      .then((cities) => {
        if (isActive) {
          setCityOptions(cities);
        }
      })
      .catch((error) => {
        console.error("Error fetching cities:", error);
        if (isActive) {
          setCityOptions([]);
          toast.error("Não foi possível carregar as cidades deste estado");
        }
      })
      .finally(() => {
        if (isActive) {
          setLoadingCities(false);
        }
      });

    return () => {
      isActive = false;
    };
  }, [formData.state_uf]);

  const toggleArea = (area) => {
    setFormData((prev) => ({
      ...prev,
      areas_supported: prev.areas_supported.includes(area)
        ? prev.areas_supported.filter((item) => item !== area)
        : [...prev.areas_supported, area],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!consent) {
      toast.error("Você precisa aceitar os termos para seguir");
      return;
    }

    if (user) {
      toast.error("Saia da conta atual antes de cadastrar um novo consultor parceiro");
      return;
    }

    if (formData.areas_supported.length === 0) {
      toast.error("Selecione pelo menos uma área de atuação");
      return;
    }

    setLoading(true);
    try {
      await registerConsultant({
        name: formData.name.trim(),
        email: formData.email.trim(),
        password: formData.password,
        profile: {
          areas_supported: formData.areas_supported,
          city: formData.city ? buildCityLabel(formData.city.trim(), formData.state_uf) : null,
          online_only: !!formData.online_only,
          bio: formData.bio.trim() || null,
          consultant_split_percent: 70,
          company_split_percent: 30,
        },
      });

      toast.success("Cadastro de consultor realizado com sucesso!");
      navigate("/consultant");
    } catch (error) {
      console.error("Consultant partner register error:", error);
      toast.error(error.response?.data?.detail || "Erro ao cadastrar consultor parceiro");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <div className="w-full max-w-3xl">
        <Link to="/" className="inline-flex items-center text-slate-600 hover:text-navy mb-8 transition-colors">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Voltar ao início
        </Link>

        <Card className="border-0 shadow-xl brand-surface">
          <CardHeader className="pb-2">
            <BrandLogo size="lg" className="mb-4" />
            <CardTitle className="font-serif text-3xl text-navy">Cadastro de Consultor Parceiro</CardTitle>
            <CardDescription>
              Preencha seus dados para entrar na base de consultores parceiros da Credita CGE.
            </CardDescription>
          </CardHeader>

          <CardContent className="pt-6">
            {user && (
              <div className="mb-6 rounded-lg border border-amber-200 bg-amber-50 p-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5" />
                  <div className="space-y-3">
                    <div>
                      <p className="text-sm font-semibold text-amber-900">Você já está logado no sistema</p>
                      <p className="text-sm text-amber-800">
                        Para usar este link como cadastro público de consultor parceiro, saia da conta atual ou abra em aba anônima.
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-3">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={async () => {
                          await logout();
                          toast.success("Sessão encerrada. Agora você pode usar o cadastro do consultor parceiro.");
                        }}
                      >
                        Sair da conta atual
                      </Button>
                      <Button type="button" variant="ghost" onClick={() => navigate(-1)}>
                        Voltar
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="consultant-name">Nome completo</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                    <Input
                      id="consultant-name"
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                      className="pl-10"
                      placeholder="Seu nome"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="consultant-email">E-mail</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                    <Input
                      id="consultant-email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
                      className="pl-10"
                      placeholder="seu@email.com"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="consultant-password">Senha</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                    <Input
                      id="consultant-password"
                      type="password"
                      value={formData.password}
                      onChange={(e) => setFormData((prev) => ({ ...prev, password: e.target.value }))}
                      className="pl-10"
                      placeholder="Mínimo 6 caracteres"
                      minLength={6}
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="consultant-state">UF</Label>
                  <Select
                    value={formData.state_uf}
                    onValueChange={(value) => setFormData((prev) => ({ ...prev, state_uf: value, city: "" }))}
                  >
                    <SelectTrigger id="consultant-state">
                      <SelectValue placeholder="Selecione a UF" />
                    </SelectTrigger>
                    <SelectContent>
                      {BRAZILIAN_STATES.map((state) => (
                        <SelectItem key={state.uf} value={state.uf}>
                          {state.uf}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="consultant-city">Cidade</Label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-3 h-4 w-4 text-slate-400 z-10" />
                  <Select
                    value={formData.city}
                    onValueChange={(value) => setFormData((prev) => ({ ...prev, city: value }))}
                    disabled={!formData.state_uf || loadingCities}
                  >
                    <SelectTrigger id="consultant-city" className="pl-10">
                      <SelectValue placeholder={loadingCities ? "Carregando cidades..." : "Selecione a cidade"} />
                    </SelectTrigger>
                    <SelectContent>
                      {cityOptions.map((city) => (
                        <SelectItem key={city} value={city}>
                          {city}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Briefcase className="h-4 w-4 text-navy" />
                  <Label className="text-base font-semibold text-navy">Áreas de atuação</Label>
                </div>
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {AREAS.map((area) => (
                    <label key={area} className="flex items-center gap-3 rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 cursor-pointer">
                      <Checkbox
                        checked={formData.areas_supported.includes(area)}
                        onCheckedChange={() => toggleArea(area)}
                      />
                      <span>{getAreaLabel(area)}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="rounded-lg border border-slate-200 bg-white p-4">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <p className="text-sm font-medium text-navy">Atendimento online</p>
                    <p className="text-xs text-slate-500">Marque se você atende parceiros de qualquer cidade.</p>
                  </div>
                  <Switch
                    checked={formData.online_only}
                    onCheckedChange={(checked) => setFormData((prev) => ({ ...prev, online_only: checked }))}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="consultant-bio">Apresentação profissional</Label>
                <Textarea
                  id="consultant-bio"
                  value={formData.bio}
                  onChange={(e) => setFormData((prev) => ({ ...prev, bio: e.target.value }))}
                  placeholder="Conte um pouco sobre sua experiência, perfil de atendimento e diferenciais."
                  className="min-h-[120px]"
                />
              </div>

              <div className="flex items-start space-x-3 relative z-10">
                <Checkbox
                  id="consultant-consent"
                  checked={consent}
                  onCheckedChange={setConsent}
                  className="mt-0.5"
                />
                <Label htmlFor="consultant-consent" className="text-sm text-slate-600 leading-tight cursor-pointer select-none">
                  Confirmo que as informações enviadas estão corretas e aceito os termos para cadastro como consultor parceiro.
                </Label>
              </div>

              <Button type="submit" className="w-full bg-navy hover:bg-navy/90" disabled={loading || !!user}>
                {loading ? "Cadastrando..." : "Cadastrar como consultor parceiro"}
              </Button>
            </form>

            <div className="my-6 flex items-center gap-4">
              <Separator className="flex-1" />
              <span className="text-sm text-slate-500">já tem acesso?</span>
              <Separator className="flex-1" />
            </div>

            <p className="text-center text-sm text-slate-600">
              Já é parceiro?{" "}
              <Link to="/login" className="text-navy font-medium hover:underline">
                Entrar no sistema
              </Link>
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ConsultantPartnerRegisterPage;
